import { FC, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import moment from 'moment';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import useRoiRequestScheduleOverTime from 'hooks/useRoiRequestScheduleOverTime';
import {
  LABEL_FORMAT_BY_GRANULARITY,
  bucketRequestsSchedule,
  getGranularityForRange,
} from 'utils/bucketTimeSeries';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

interface RequestsScheduleOverTimeProps {
  selectedDateRange: { fromDate: string; toDate: string };
  hourType: string;
  clinicIds?: string[];
}

const OTHERS_COLOR = '#B01BAE';
const FIRSTIN_LINKS_COLOR = '#761972';
const GOOGLE_BOOK_ONLINE_COLOR = '#E6A6E5';
const BAR_THICKNESS = 55;

const RequestsScheduleOverTime: FC<RequestsScheduleOverTimeProps> = ({
  selectedDateRange,
  hourType,
  clinicIds,
}) => {
  const { data, isLoading, isValidating } = useRoiRequestScheduleOverTime({
    startDate: selectedDateRange.fromDate,
    endDate: selectedDateRange.toDate,
    hourType,
    clinicIds,
  });

  const { chartData, bucketed, granularity } = useMemo(() => {
    const entries = data?.periodData?.requestsSchedule ?? [];
    const resolvedGranularity = getGranularityForRange(
      selectedDateRange.fromDate,
      selectedDateRange.toDate
    );
    const bucketedEntries = bucketRequestsSchedule(
      entries,
      resolvedGranularity
    );
    const labelFormat = LABEL_FORMAT_BY_GRANULARITY[resolvedGranularity];

    return {
      bucketed: bucketedEntries,
      granularity: resolvedGranularity,
      chartData: {
        labels: bucketedEntries.map((d) => moment(d.date).format(labelFormat)),
        datasets: [
          {
            label: 'Others',
            data: bucketedEntries.map((d) => d.others),
            backgroundColor: OTHERS_COLOR,
            borderRadius: 2,
            stack: 'requests',
            barThickness: BAR_THICKNESS,
          },
          {
            label: 'FirstIn Links',
            data: bucketedEntries.map((d) => d.firstInLinks),
            backgroundColor: FIRSTIN_LINKS_COLOR,
            borderRadius: 2,
            stack: 'requests',
            barThickness: BAR_THICKNESS,
          },
          {
            label: 'Google Book Online',
            data: bucketedEntries.map((d) => d.googleBookOnline),
            backgroundColor: GOOGLE_BOOK_ONLINE_COLOR,
            borderRadius: 2,
            stack: 'requests',
            barThickness: BAR_THICKNESS,
          },
        ],
      },
    };
  }, [data, selectedDateRange.fromDate, selectedDateRange.toDate]);

  return (
    <div className="p-2.4 shadow-elevation-06-hover bg-white min-h-[37rem] flex flex-col">
      <div className="flex items-center justify-between mb-1.6">
        <span className="text-16 font-bold leading-[2.4rem]">
          Requests Schedule Over Time
        </span>
        <div className="flex items-center gap-x-1.6 text-12 text-darkest-grey">
          <div className="flex items-center gap-x-0.6">
            <span
              className="w-0.8 h-0.8 rounded-full inline-block flex-shrink-0"
              style={{ backgroundColor: FIRSTIN_LINKS_COLOR }}
            />
            <span>FirstIn Links</span>
          </div>
          <div className="flex items-center gap-x-0.6">
            <span
              className="w-0.8 h-0.8 rounded-full inline-block flex-shrink-0"
              style={{ backgroundColor: GOOGLE_BOOK_ONLINE_COLOR }}
            />
            <span>Google Book Online</span>
          </div>
          <div className="flex items-center gap-x-0.6">
            <span
              className="w-0.8 h-0.8 rounded-full inline-block flex-shrink-0"
              style={{ backgroundColor: OTHERS_COLOR }}
            />
            <span>Others</span>
          </div>
        </div>
      </div>

      {isLoading || isValidating ? (
        <div className="flex items-center justify-center flex-1">
          <LoadingSpinner className="all-child:fill-magenta w-4 h-4" />
        </div>
      ) : (
        <div className="flex-1">
          <Bar
            data={chartData}
            options={{
              maintainAspectRatio: false,
              plugins: {
                legend: { display: false },
                tooltip: {
                  enabled: true,
                  callbacks: {
                    title: (items) => {
                      const index = items[0]?.dataIndex ?? 0;
                      const entry = bucketed[index];
                      if (granularity === 'day' && entry) {
                        return moment(entry.date).format('YYYY/MM/DD');
                      }
                      return items[0]?.label ?? '';
                    },
                  },
                },
              },
              scales: {
                x: {
                  stacked: true,
                  grid: { display: false },
                  ticks: {
                    color: '#373737',
                    font: { size: 12 },
                    autoSkip: true,
                    maxRotation: 0,
                  },
                },
                y: {
                  stacked: true,
                  grid: { color: '#f0f0f0' },
                  ticks: {
                    color: '#373737',
                    font: { size: 12 },
                    precision: 0,
                    callback: (value: any) =>
                      Number.isInteger(value) ? value : null,
                  },
                },
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

export default RequestsScheduleOverTime;
